import { useCallback, useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { AlertCircle, AlertTriangle, RefreshCw, Search, ShieldAlert } from "lucide-react"
import {
  smartoltService,
  type SmartOLTDiagnostics,
  type SmartOLTDiagnosticsEvent,
  type SmartOLTDiagnosticsTab,
} from "../../../services/smartolt.service.ts"
import { toast } from "../../../utils/notify.ts"
import { SMARTOLT_PATHS } from "../smartoltNav.ts"
import {
  DIAGNOSTICS_TABS,
  filterEvents,
  formatDiagnosticDate,
  messageToneClass,
  severityBadgeClass,
} from "./smartoltDiagnosticsUtils.ts"

const empty: SmartOLTDiagnostics = {
  tab: "emergency_critical",
  split_view: true,
  critical_events: [],
  warning_events: [],
  events: [],
  reload_seconds: 120,
  updated_at: null,
  counts: {},
  filtered_count: 0,
}

function severityLightClass(severity: string) {
  const s = (severity || "").toLowerCase()
  if (["critical", "emergency"].includes(s)) return "bg-rose-50 text-rose-700 border border-rose-200"
  if (["alert", "error"].includes(s)) return "bg-amber-50 text-amber-700 border border-amber-200"
  if (s === "warning") return "bg-amber-50 text-amber-700 border border-amber-200"
  if (["notice", "informational"].includes(s)) return "bg-blue-50 text-blue-700 border border-blue-200"
  return "bg-slate-100 text-slate-700 border border-slate-200"
}

function messageLightClass(tone?: string) {
  if (!tone) return "text-slate-700"
  if (tone === "critical") return "text-rose-700 font-medium"
  if (tone === "warning") return "text-amber-800"
  return "text-slate-700"
}

function EventTable({
  title,
  rows,
  search,
  icon: Icon,
  accentColor,
}: {
  title: string
  rows: SmartOLTDiagnosticsEvent[]
  search: string
  icon?: React.ComponentType<any>
  accentColor?: string
}) {
  const filtered = useMemo(() => filterEvents(rows, search), [rows, search])

  return (
    <div className="min-w-0 flex-1 overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-xs">
      <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50/60 px-4 py-3">
        {Icon && (
          <div className={`flex h-7 w-7 items-center justify-center rounded-lg ${accentColor || "bg-slate-100 text-slate-600 border border-slate-200"}`}>
            <Icon className="h-3.5 w-3.5 text-current" />
          </div>
        )}
        <h2 className="text-sm font-bold text-slate-900">{title}</h2>
        <span className="ml-auto rounded-full bg-slate-100 border border-slate-200 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
          {filtered.length}
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-xs">
          <thead className="border-b border-slate-200 bg-slate-50/80">
            <tr>
              {["OLT", "ONT", "IP Address", "Date", "Message", "Severity"].map((h) => (
                <th key={h} className="px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-600">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-10 text-center text-slate-400 text-sm">
                  No events in this window.
                </td>
              </tr>
            ) : (
              filtered.map((row) => (
                <tr key={row.id} className="transition-colors hover:bg-slate-50/80">
                  <td className="px-3 py-2.5">
                    {row.olt_id ? (
                      <Link to={SMARTOLT_PATHS.oltDetail(row.olt_id)} className="font-semibold text-blue-600 hover:text-blue-800 hover:underline">
                        {row.olt_name || row.olt_id}
                      </Link>
                    ) : (
                      <span className="text-slate-600">{row.olt_name || "—"}</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    {row.onu_external_id ? (
                      <Link to={SMARTOLT_PATHS.onuDetail(row.onu_external_id)} className="font-semibold text-blue-600 hover:text-blue-800 hover:underline">
                        {row.onu_name || row.onu_external_id}
                      </Link>
                    ) : (
                      <span className="text-slate-600">{row.onu_name || "—"}</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 font-mono text-slate-600">{row.ip_address || "—"}</td>
                  <td className="whitespace-nowrap px-3 py-2.5 font-mono text-slate-500">{formatDiagnosticDate(row.date)}</td>
                  <td className={`max-w-[280px] px-3 py-2.5 ${messageLightClass(row.tone)}`}>{row.message}</td>
                  <td className="px-3 py-2.5">
                    <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold capitalize ${severityLightClass(row.severity)}`}>
                      {row.severity}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export function SmartOLTDiagnosticsPage() {
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [data, setData] = useState<SmartOLTDiagnostics>(empty)
  const [tab, setTab] = useState<SmartOLTDiagnosticsTab>("emergency_critical")
  const [search, setSearch] = useState("")
  const [reloadIn, setReloadIn] = useState(empty.reload_seconds)

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true)
    setRefreshing(true)
    try {
      const next = await smartoltService.getDiagnostics({ tab, range: "24h" })
      setData({
        tab: next?.tab || tab,
        split_view: Boolean(next?.split_view),
        critical_events: Array.isArray(next?.critical_events) ? next.critical_events : [],
        warning_events: Array.isArray(next?.warning_events) ? next.warning_events : [],
        events: Array.isArray(next?.events) ? next.events : [],
        reload_seconds: next?.reload_seconds || 120,
        updated_at: next?.updated_at || null,
        counts: next?.counts || {},
        filtered_count: next?.filtered_count || 0,
      })
      setReloadIn(next?.reload_seconds || 120)
    } catch {
      toast.error("Failed to load diagnostics")
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [tab])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    const timer = window.setInterval(() => {
      setReloadIn((prev) => {
        if (prev <= 1) {
          load(true)
          return data.reload_seconds
        }
        return prev - 1
      })
    }, 1000)
    return () => window.clearInterval(timer)
  }, [load, data.reload_seconds])

  const showSplit = data.split_view && tab === "emergency_critical"

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Diagnostics</h1>
          <p className="mt-0.5 text-xs text-slate-500">Live OLT/ONT events from SmartOLT sync</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
          <span className="rounded-lg bg-white border border-slate-200 px-2.5 py-1 font-semibold text-slate-700 shadow-xs">
            {data.filtered_count} events
          </span>
          {data.updated_at && (
            <span className="text-slate-500">· updated {formatDiagnosticDate(data.updated_at)}</span>
          )}
          <span className="inline-flex items-center gap-1 text-slate-500">
            · reload in{" "}
            <span className={`font-mono font-bold ${reloadIn <= 10 ? "text-amber-600" : "text-slate-700"}`}>{reloadIn}s</span>
          </span>
          <button type="button" onClick={() => load(true)} disabled={refreshing}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 font-semibold text-slate-700 shadow-xs hover:bg-slate-50 disabled:opacity-60 transition-colors">
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Tab filter toolbar */}
      <div className="overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-xs">
        <div className="flex flex-wrap items-center gap-2 p-3">
          <div className="flex flex-wrap gap-1">
            {DIAGNOSTICS_TABS.map((item) => (
              <button key={item.id} type="button" onClick={() => setTab(item.id)}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${
                  tab === item.id
                    ? "bg-blue-600 text-white shadow-xs"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200/70 hover:text-slate-900"
                }`}>
                {item.label}
                {data.counts[item.id] ? (
                  <span className={`ml-1.5 rounded-full px-1.5 py-0.5 text-[10px] leading-none font-bold ${
                    tab === item.id ? "bg-white/25 text-white" : "bg-slate-200 text-slate-700"
                  }`}>
                    {data.counts[item.id]}
                  </span>
                ) : ""}
              </button>
            ))}
          </div>
          <div className="relative ml-auto min-w-[180px] flex-1 sm:max-w-xs">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search events…"
              className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-8 pr-3 text-sm text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Event tables */}
      {loading ? (
        <div className="rounded-xl border border-slate-200/80 bg-white px-4 py-12 text-center shadow-xs">
          <RefreshCw className="mx-auto h-7 w-7 animate-spin text-blue-500 mb-2" />
          <p className="text-slate-500 text-sm font-medium">Loading diagnostics…</p>
        </div>
      ) : showSplit ? (
        <div className="flex flex-col gap-3 xl:flex-row">
          <EventTable
            title="Emergency, Critical & Alert"
            rows={data.critical_events}
            search={search}
            icon={ShieldAlert}
            accentColor="bg-rose-50 text-rose-600 border border-rose-200"
          />
          <EventTable
            title="Error & Warning events"
            rows={data.warning_events}
            search={search}
            icon={AlertTriangle}
            accentColor="bg-amber-50 text-amber-600 border border-amber-200"
          />
        </div>
      ) : (
        <EventTable
          title="Diagnostic events"
          rows={data.events}
          search={search}
          icon={AlertCircle}
          accentColor="bg-blue-50 text-blue-600 border border-blue-200"
        />
      )}
    </div>
  )
}
